import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { ErrorMessage, Field, FieldGroup, Fieldset, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { PrimaryButton } from "src/components/base/button";
import { useForm } from "@tanstack/react-form";
import { SimpleTag } from "src/api/generated/frontend_internal_v1";
import { ApiInternal } from "src/api/api_internal";

/**
 * The properties for {@link RenameTagDialog}
 */
export type RenameTagDialogProps = {
    /** Callback that should be called when closing the popup */
    onClose: () => void;

    /** Callback that should be called when the form has been submitted successfully */
    onSubmit: () => void;

    /** The tag to rename */
    tag: SimpleTag;
};

/**
 * A dialog to rename a device
 */
export default function RenameTagDialog(props: RenameTagDialogProps) {
    const [t] = useTranslation("dialog-rename-tag");

    const form = useForm({
        defaultValues: { name: props.tag.name },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const res = await ApiInternal.tags.setName(props.tag.uuid, value.name);

                if (res.result === "Err") {
                    return {
                        fields: {
                            name: res.error.name_not_unique ? t("error.name-not-unique") : undefined,
                        },
                    };
                }
                props.onSubmit();
            },
        },
    });

    return (
        <Dialog open={true} onClose={() => props.onClose()}>
            <DialogTitle>{t("heading.rename-tag", { old: props.tag.name })}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"name"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.new-name")}</RequiredLabel>
                                        <Input
                                            autoFocus={true}
                                            required={true}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            pattern={"[a-z0-9]+(-*[a-z0-9]+)*"}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                            <DialogActions>
                                <PrimaryButton type={"submit"}>{t("button.rename-tag")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
